"use client";

import { useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { override, pickSourceContent, type SourcedBlok } from "@/lib/sourced-blok";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { useMediaQuery } from "@/lib/use-media-query";

type Asset = { filename?: string; alt?: string };

type GlobalCardWithIcon = SbBlokData & {
  icon?: Asset;
  title?: string;
  description?: string;
};

type GlobalCardsWithIconsFields = {
  title?: string;
  cards?: GlobalCardWithIcon[];
  link_label?: string;
  link_url?: StoryblokLink;
  bg_color?: string;
};

type GlobalCardsWithIconsBlok = SbBlokData & SourcedBlok<GlobalCardsWithIconsFields>;

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={`transition-transform duration-200 ease-out ${open ? "rotate-90" : ""}`}
    >
      <path
        d="M7.5 5l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function GlobalCardsWithIcons({ blok }: { blok: GlobalCardsWithIconsBlok }) {
  const src = pickSourceContent(blok);
  const title = override(blok.title, src?.title);
  const cards = override(blok.cards, src?.cards) ?? [];
  const linkLabel = override(blok.link_label, src?.link_label);
  const linkUrl = override(blok.link_url, src?.link_url);
  const bgColor = override(blok.bg_color, src?.bg_color);

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isWide = useMediaQuery("(min-width: 680px)");
  const { bg, onDark } = resolveBg(bgColor, "dusk");
  const region = regionFromPathname(usePathname());
  const link = resolveLink(linkUrl, region);
  const showLink = Boolean(linkLabel && link.href !== "#");

  return (
    <section
      {...storyblokEditable(blok)}
      className={`${bg} ${onDark ? "text-white" : "text-black"}`}
    >
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(2.5rem,5vw,3.75rem)] pb-[clamp(2rem,5vw,3.75rem)] lg:py-[60px]">
        {title && (
          <h2 className="text-purple-05 text-center text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold">
            {title}
          </h2>
        )}

        {cards.length > 0 && (
          <ul
            className={`mt-[clamp(2rem,4vw,3rem)] grid grid-cols-1 gap-2 [@media(min-width:680px)]:gap-4 ${
              cards.length < 4
                ? "[@media(min-width:680px)]:grid-cols-3"
                : "[@media(min-width:680px)]:grid-cols-2"
            }`}
          >
            {cards.map((card, i) => {
              const open = openIndex === i;
              const hasDescription = Boolean(card.description);
              const isThreeUp = cards.length < 4;
              // scale-[1.35] compensates for built-in SVG viewBox padding.
              const icon = card.icon?.filename ? (
                <Image
                  src={card.icon.filename}
                  alt={card.icon.alt || ""}
                  width={144}
                  height={144}
                  className={
                    isWide
                      ? `shrink-0 scale-[1.35] object-contain ${
                          isThreeUp ? "size-[104px] lg:size-[140px]" : "size-[72px] lg:size-[112px]"
                        }`
                      : "size-[72px] shrink-0 scale-[1.35] object-contain"
                  }
                />
              ) : null;

              if (isWide) {
                return (
                  <li
                    key={card._uid}
                    {...storyblokEditable(card)}
                    className={`flex rounded-[8px] bg-white p-[13px] lg:rounded-[12px] lg:p-8 ${
                      isThreeUp
                        ? "flex-col items-center gap-5 text-center lg:gap-6"
                        : "items-start gap-4 lg:gap-7"
                    }`}
                  >
                    {icon}
                    <div className="flex flex-1 flex-col gap-5">
                      <span className="text-h6 lg:text-h5 leading-tight font-bold whitespace-pre-line text-black">
                        {card.title}
                      </span>
                      {hasDescription && (
                        <p className="text-body leading-[1.64] text-pretty text-black">
                          {card.description}
                        </p>
                      )}
                    </div>
                  </li>
                );
              }

              // Mobile: a compact accordion with the description spanning the
              // full card width below the icon + title.
              return (
                <li
                  key={card._uid}
                  {...storyblokEditable(card)}
                  className="rounded-[8px] bg-white p-[13px]"
                >
                  <button
                    type="button"
                    onClick={() => hasDescription && setOpenIndex(open ? null : i)}
                    aria-expanded={hasDescription ? open : undefined}
                    disabled={!hasDescription}
                    className="flex w-full items-center gap-4 text-left"
                  >
                    {icon}
                    <span className="text-h6 flex-1 leading-tight font-bold whitespace-pre-line text-black">
                      {card.title}
                    </span>
                    {hasDescription && (
                      <span className="text-purple-03 flex size-5 shrink-0 items-center justify-center">
                        <ChevronIcon open={open} />
                      </span>
                    )}
                  </button>
                  {hasDescription && (
                    <div
                      inert={!open}
                      className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="text-body pt-3 leading-[1.64] text-pretty text-black">
                          {card.description}
                        </p>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {showLink && (
          <div className="mt-6 flex justify-center lg:mt-10">
            <a
              href={link.href}
              {...(link.target ? { target: link.target } : {})}
              className="text-body text-purple-04 inline-flex items-center gap-2 py-2.5 font-extrabold hover:underline"
            >
              {linkLabel}
              <ArrowIcon />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
